import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent, CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} myEmailButton - Hidden input for my email option
 * @property {HTMLInputElement} recipientEmailButton - Hidden input for recipient email option
 * @property {HTMLDivElement} deliveryDropdown - Dropdown container
 * @property {HTMLButtonElement} dropdownTrigger - Dropdown trigger button
 * @property {HTMLSpanElement} dropdownLabel - Dropdown label text
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} recipientSendOn - Send on date input
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLInputElement} [controlFlag] - Shopify gift card control flag (optional as it's dynamically queried)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [messageError] - Message error message container (optional)
 * @property {HTMLDivElement} [sendOnError] - Send on error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 * @property {HTMLInputElement} [physicalGiftCardId] - Physical gift card ID input (optional)
 * @property {HTMLInputElement} [physicalGiftCardParentId] - Physical gift card parent ID input (optional)
 * @property {HTMLInputElement} [physicalGiftCardType] - Physical gift card type input (optional)
 * 
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  #currentMode = GiftCardRecipientForm.DeliveryMode.SELF;

  // Flag to track if recipient option is currently disabled
  #isRecipientOptionDisabled = false;

  // Store bound event handlers for cleanup
  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: import('@theme/events').CartErrorEvent) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;
  /** @type {((event: Event) => void) | null} */
  #handleDocumentClickBound = null;
  /** @type {((event: import('@theme/events').VariantUpdateEvent) => void) | null} */
  #handleVariantUpdateBound = null;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'deliveryDropdown',
    'dropdownTrigger',
    'dropdownLabel',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'recipientMessage',
    'recipientSendOn',
  ];

  /**
   * Get all recipient input fields
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [this.refs.recipientEmail, this.refs.recipientName, this.refs.recipientMessage, this.refs.recipientSendOn];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();
    this.#initializeDropdown();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - #displayCartErrorBound is guaranteed to be non-null here and accepts CartErrorEvent
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = () => this.#handleCartAdd();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);

    this.#handleDocumentClickBound = this.#handleDocumentClick.bind(this);
    document.addEventListener('click', this.#handleDocumentClickBound);

    this.#handleVariantUpdateBound = this.#handleVariantUpdate.bind(this);
    const target = this.closest('.shopify-section, dialog, product-card');
    // @ts-ignore - addEventListener accepts custom events
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateBound);

    // Check initial variant state
    this.#checkInitialVariantType();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      // @ts-ignore - removeEventListener accepts custom events
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }

    if (this.#handleDocumentClickBound) {
      document.removeEventListener('click', this.#handleDocumentClickBound);
      this.#handleDocumentClickBound = null;
    }

    if (this.#handleVariantUpdateBound) {
      const target = this.closest('.shopify-section, dialog, product-card');
      // @ts-ignore - removeEventListener accepts custom events
      target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateBound);
      this.#handleVariantUpdateBound = null;
    }
  }

  /**
   * Initialize dropdown functionality
   */
  #initializeDropdown() {
    const dropdown = this.refs.deliveryDropdown;
    const trigger = this.refs.dropdownTrigger;
    const menu = dropdown.querySelector('.recipient-dropdown__menu');
    const items = dropdown.querySelectorAll('.recipient-dropdown__item');

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = dropdown.dataset.open === 'true';
      dropdown.dataset.open = isOpen ? 'false' : 'true';
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });

    items.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const value = /** @type {HTMLElement} */ (item).dataset.value;
        if (value) {
          this.#selectDropdownItem(item, value);
        }
      });

      item.addEventListener('keydown', (e) => {
        if (e instanceof KeyboardEvent && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          /** @type {HTMLElement} */ (item).click();
        }
      });
    });
  }

  /**
   * Handle dropdown item selection
   * @param {Element} item - The selected item
   * @param {string} value - The value of the selected item
   */
  #selectDropdownItem(item, value) {
    // Check if the item is disabled
    if (item.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const dropdown = this.refs.deliveryDropdown;
    const trigger = this.refs.dropdownTrigger;
    const label = this.refs.dropdownLabel;
    const items = dropdown.querySelectorAll('.recipient-dropdown__item');

    label.textContent = item.textContent;

    items.forEach((i) => {
      i.classList.remove('recipient-dropdown__item--selected');
      i.removeAttribute('aria-selected');
    });
    item.classList.add('recipient-dropdown__item--selected');
    item.setAttribute('aria-selected', 'true');

    dropdown.dataset.open = 'false';
    trigger.setAttribute('aria-expanded', 'false');

    this.toggleRecipientForm(value, undefined);
  }

  /**
   * Handle document click to close dropdown
   * @param {Event} event - The click event
   */
  #handleDocumentClick(event) {
    if (event.target && event.target instanceof Element && !event.target.closest('.recipient-dropdown')) {
      const dropdown = this.refs.deliveryDropdown;
      const trigger = this.refs.dropdownTrigger;
      dropdown.dataset.open = 'false';
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Initialize form with default state, self delivery is selected by default
   */
  #initializeForm() {
    this.#updateButtonStates(GiftCardRecipientForm.DeliveryMode.SELF);

    this.refs.recipientFields.hidden = true;

    this.#clearRecipientFields();
    this.#disableRecipientFields();
    this.#setDateConstraints();
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} [_event] - Change event (unused, optional)
   */
  toggleRecipientForm(mode, _event) {
    // Validate mode
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', ',
        )}`,
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /**
   * Update form state based on current mode
   */
  #updateFormState() {
    const { DeliveryMode } = GiftCardRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();

      this.#updateCharacterCount();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      // Focus first field for accessibility
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      }),
    );
  }

  /**
   * Update dropdown UI states
   * @param {string} mode - Current delivery mode
   */
  #updateButtonStates(mode) {
    const { DeliveryMode } = GiftCardRecipientForm;
    const dropdown = this.refs.deliveryDropdown;
    const items = dropdown.querySelectorAll('.recipient-dropdown__item');
    const label = this.refs.dropdownLabel;

    items.forEach((item) => {
      const htmlItem = /** @type {HTMLElement} */ (item);
      const isSelected = htmlItem.dataset.value === mode;
      item.classList.toggle('recipient-dropdown__item--selected', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected) {
        label.textContent = item.textContent;
      }
    });

    switch (mode) {
      case DeliveryMode.SELF:
        this.refs.myEmailButton.value = 'self';
        break;

      case DeliveryMode.RECIPIENT:
        this.refs.myEmailButton.value = 'recipient_form';
        break;

      default:
        console.warn(`Unknown delivery mode: ${mode}`);
        this.refs.myEmailButton.value = 'self';
    }
  }

  /**
   * Clear all recipient form fields
   */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();

    if (this.refs.recipientSendOn.type === 'date' && document.activeElement !== this.refs.recipientSendOn) {
      this.refs.recipientSendOn.type = 'text';
    }
  }

  /**
   * Disable recipient form fields when sending to self
   */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Remove control field when sending to self
    const controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (controlFlag) {
      controlFlag.remove();
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  /**
   * Enable recipient form fields when sending to recipient
   */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      if (field === this.refs.recipientEmail) {
        field.setAttribute('required', 'required');
      }
    }

    // Add control field when sending to recipient
    let controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (!controlFlag) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'on';
      this.appendChild(input);
    }

    // Enable and set timezone offset
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    // Set date constraints when enabling fields
    this.#setDateConstraints();
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;
  }

  /**
   * Set date constraints for the send on date picker
   * Prevents selecting past dates and limits to 90 days in the future
   */
  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    // Format dates as YYYY-MM-DD
    /**
     * @param {Date} date
     * @returns {string}
     */
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.refs.recipientSendOn.setAttribute('min', formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));

    this.#initSendOnTypeSwap();
  }

  /**
   * Swap the send-on input between text and date types.
   *
   * The field renders as type="text" so it shows a single, normal placeholder
   * (the localized "Send on" label) instead of the browser's native date
   * placeholder (e.g. Firefox's "дд.мм.гггг"), which previously overlapped the
   * custom placeholder. On focus it becomes a real date input (opening the
   * native picker); on blur it reverts to text only while empty.
   */
  #initSendOnTypeSwap() {
    const input = this.refs.recipientSendOn;
    if (input.dataset.typeSwapInitialized === 'true') return;
    input.dataset.typeSwapInitialized = 'true';

    const toDate = () => {
      if (input.type !== 'date') input.type = 'date';
    };

    input.addEventListener('focus', () => {
      toDate();
      // Open the native picker where supported (Chromium/Safari).
      if (typeof input.showPicker === 'function') {
        try {
          input.showPicker();
        } catch {
          // showPicker can throw if not triggered by a user gesture; ignore.
        }
      }
    });

    input.addEventListener('blur', () => {
      if (!input.value) input.type = 'text';
    });
  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /**
   * Clear all error messages and reset ARIA attributes
   */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['emailError', 'nameError', 'messageError', 'sendOnError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  #handleCartAdd() {
    this.#clearErrorMessages();
  }

  /**
   * Check the initial variant type on component load
   */
  #checkInitialVariantType() {
    // Get the initial variant options from data attribute
    const variantOptions = this.dataset.productVariantOptions?.split(',') || [];
    if (!variantOptions.length) return;

    const isPhysical = this.#checkIfPhysicalFromVariant(variantOptions);
    this.#updateGiftCardTypeState(isPhysical);
  }

  /**
   * Handle variant update events
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    const isPhysical = this.#checkIfPhysicalFromVariant(variant?.options || []);
    this.#updateGiftCardTypeState(isPhysical);

    if (isPhysical && this.refs.physicalGiftCardParentId) {
      this.refs.physicalGiftCardParentId.value = variant.id;
    }
  }
  /**
   * Check if a variant object contains "physical" type
   * @param {Array<string>} variantOptions - The options of the variant
   * @returns {boolean} True if the variant is physical
   */
  #checkIfPhysicalFromVariant(variantOptions) {
    if (variantOptions?.includes('Physical')) return true;
    else return false;
  }

  /**
   * Update the gift card type state (enable/disable recipient option)
   * @param {boolean} isPhysical - Whether the gift card is physical
   */
  #updateGiftCardTypeState(isPhysical) {
    if (isPhysical) {
      this.#enableRecipientOption();

      // Enable physical gift card inputs
      this.#togglePhysicalGiftCardInputs(false);
    } else {
      // Digital gift card: enable recipient option
      this.#enableRecipientOption();

      // Disable physical gift card inputs
      this.#togglePhysicalGiftCardInputs(true);
    }
  }

  /**
   * Disable the recipient option in the dropdown
   */
  #disableRecipientOption() {
    if (this.#isRecipientOptionDisabled) return;

    const dropdown = this.refs.deliveryDropdown;
    const recipientItem = /** @type {HTMLElement | null} */ (
      dropdown.querySelector('.recipient-dropdown__item[data-value="recipient_form"]')
    );

    if (recipientItem) {
      recipientItem.style.pointerEvents = 'none';
      recipientItem.style.opacity = '0.5';
      recipientItem.setAttribute('aria-disabled', 'true');
      this.#isRecipientOptionDisabled = true;
    }
  }

  /**
   * Enable the recipient option in the dropdown
   */
  #enableRecipientOption() {
    if (!this.#isRecipientOptionDisabled) return;

    const dropdown = this.refs.deliveryDropdown;
    const recipientItem = /** @type {HTMLElement | null} */ (
      dropdown.querySelector('.recipient-dropdown__item[data-value="recipient_form"]')
    );

    if (recipientItem) {
      recipientItem.style.pointerEvents = '';
      recipientItem.style.opacity = '';
      recipientItem.removeAttribute('aria-disabled');
      this.#isRecipientOptionDisabled = false;
    }
  }

  /**
   * Force select the "self" mode
   */
  #forceSelectSelfMode() {
    const dropdown = this.refs.deliveryDropdown;
    const selfItem = dropdown.querySelector('.recipient-dropdown__item[data-value="self"]');

    if (selfItem) {
      this.#selectDropdownItem(selfItem, 'self');
    }
  }

  /**
   * Toggle physical gift card inputs
   * @param {boolean} enable - Whether to enable or disable the inputs
   */
  #togglePhysicalGiftCardInputs(enable) {
    if (this.refs.physicalGiftCardId) {
      this.refs.physicalGiftCardId.disabled = enable;
    }
    if (this.refs.physicalGiftCardParentId) {
      this.refs.physicalGiftCardParentId.disabled = enable;
    }
    if (this.refs.physicalGiftCardType) {
      this.refs.physicalGiftCardType.disabled = enable;
    }
  }
}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
